import Phaser from "phaser";
import "./style.css";

const COLS = 25;
const ROWS = 21;
const TILE = 28;
const WIDTH = COLS * TILE;
const HEIGHT = ROWS * TILE;
const FULL_SPEED_STEP_MS = 94;

type Direction = "up" | "down" | "left" | "right";
type VirusName = "worm" | "trojan" | "spyware" | "bot";
type VirusState = "active" | "returning" | "quarantined" | "waiting";

type Cell = {
  row: number;
  col: number;
};

type Mover = Cell & {
  sprite: Phaser.GameObjects.Sprite;
  direction: Direction;
  moving: boolean;
};

type Virus = Mover & {
  name: VirusName;
  corner: Cell;
  state: VirusState;
  safeUntil: number;
  releaseDelay: number;
};

type Maze = {
  open: boolean[][];
  playerStart: Cell;
  core: Cell;
  virusStarts: Cell[];
  patches: Cell[];
  portals: PortalPair[];
};

type PortalPair = {
  a: Cell;
  b: Cell;
  color: number;
  label: string;
};

const VECTORS: Record<Direction, Cell> = {
  up: { row: -1, col: 0 },
  down: { row: 1, col: 0 },
  left: { row: 0, col: -1 },
  right: { row: 0, col: 1 }
};

const OPPOSITE: Record<Direction, Direction> = {
  up: "down",
  down: "up",
  left: "right",
  right: "left"
};

const DIRECTIONS = Object.keys(VECTORS) as Direction[];
const VIRUS_NAMES: VirusName[] = ["worm", "trojan", "spyware", "bot"];
const VIRUS_COLORS: Record<VirusName, number> = {
  worm: 0xff5e6c,
  trojan: 0xffae57,
  spyware: 0xbd72f5,
  bot: 0x4fd5e7
};

const scoreElement = requiredElement("score");
const highScoreElement = requiredElement("high-score");
const sectorElement = requiredElement("sector");
const livesElement = requiredElement("lives");
const statusElement = requiredElement("game-status");
const noticeElement = requiredElement("notice");
const pauseButton = requiredButton("pause-button");
const startDialog = requiredDialog("start-dialog");
const gameOverDialog = requiredDialog("game-over-dialog");
const finalScoreElement = requiredElement("final-score");

function requiredElement(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element;
}

function requiredButton(id: string): HTMLButtonElement {
  return requiredElement(id) as HTMLButtonElement;
}

function requiredDialog(id: string): HTMLDialogElement {
  return requiredElement(id) as HTMLDialogElement;
}

function keyOf(cell: Cell): string {
  return `${cell.row}:${cell.col}`;
}

function seededRandom(seed: number): () => number {
  let value = seed | 0;
  return () => {
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    return (value >>> 0) / 4294967296;
  };
}

function shuffle<T>(items: T[], random: () => number): T[] {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [items[index], items[other]] = [items[other], items[index]];
  }
  return items;
}

function buildMaze(sector: number): Maze {
  const random = seededRandom(0x51f15e + sector * 7919);
  const open = Array.from({ length: ROWS }, () => Array(COLS).fill(false) as boolean[]);
  const stack: Cell[] = [{ row: 1, col: 1 }];
  open[1][1] = true;

  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    const options = shuffle(
      [
        { row: current.row - 2, col: current.col },
        { row: current.row + 2, col: current.col },
        { row: current.row, col: current.col - 2 },
        { row: current.row, col: current.col + 2 }
      ].filter(
        (cell) =>
          cell.row > 0 &&
          cell.row < ROWS - 1 &&
          cell.col > 0 &&
          cell.col < COLS - 1 &&
          !open[cell.row][cell.col]
      ),
      random
    );

    if (options.length === 0) {
      stack.pop();
      continue;
    }

    const next = options[0];
    open[(current.row + next.row) / 2][(current.col + next.col) / 2] = true;
    open[next.row][next.col] = true;
    stack.push(next);
  }

  const loopCandidates: Cell[] = [];
  for (let row = 1; row < ROWS - 1; row += 1) {
    for (let col = 1; col < COLS - 1; col += 1) {
      if (open[row][col]) continue;
      const horizontal = open[row][col - 1] && open[row][col + 1];
      const vertical = open[row - 1][col] && open[row + 1][col];
      if (horizontal !== vertical) loopCandidates.push({ row, col });
    }
  }

  const loopTarget = 23 + Math.min(sector, 8);
  let loopsOpened = 0;
  for (const cell of shuffle(loopCandidates, random)) {
    open[cell.row][cell.col] = true;
    if (createsOpenBlock(open, cell)) {
      open[cell.row][cell.col] = false;
      continue;
    }
    loopsOpened += 1;
    if (loopsOpened >= loopTarget) break;
  }

  const playerStart = { row: 1, col: 1 };
  const core = nearestOpen(open, { row: Math.floor(ROWS / 2), col: Math.floor(COLS / 2) });
  const allOpen = listOpen(open);
  const virusStarts = Array.from({ length: 4 }, () => ({ ...core }));

  const reserved = new Set([keyOf(playerStart), keyOf(core), ...virusStarts.map(keyOf)]);
  const deadEnds = allOpen.filter((cell) => {
    if (reserved.has(keyOf(cell))) return false;
    return DIRECTIONS.filter((direction) => {
      const vector = VECTORS[direction];
      return open[cell.row + vector.row]?.[cell.col + vector.col] === true;
    }).length === 1;
  });
  const portals: PortalPair[] = [];
  const remainingDeadEnds = [...deadEnds];
  const portalStyles = [
    { color: 0x4fd5e7, label: "A" },
    { color: 0xbd72f5, label: "B" }
  ];

  for (const style of portalStyles) {
    if (remainingDeadEnds.length < 2) break;
    const firstIndex = Math.floor(random() * remainingDeadEnds.length);
    const first = remainingDeadEnds.splice(firstIndex, 1)[0];
    const secondIndex = remainingDeadEnds.reduce(
      (bestIndex, cell, index, cells) =>
        manhattan(cell, first) > manhattan(cells[bestIndex], first) ? index : bestIndex,
      0
    );
    const second = remainingDeadEnds.splice(secondIndex, 1)[0];
    portals.push({ a: first, b: second, ...style });
  }

  const blocked = new Set([
    ...reserved,
    ...portals.flatMap((portal) => [keyOf(portal.a), keyOf(portal.b)])
  ]);
  const patches: Cell[] = [];
  const patchCandidates = allOpen.filter((cell) => {
    if (blocked.has(keyOf(cell))) return false;
    const exits = DIRECTIONS.filter((direction) => {
      const vector = VECTORS[direction];
      return open[cell.row + vector.row]?.[cell.col + vector.col] === true;
    }).length;
    return exits >= 2 && manhattan(cell, core) >= 4 && manhattan(cell, playerStart) >= 6;
  });

  while (patches.length < 4 && patchCandidates.length > 0) {
    const anchors = [playerStart, ...patches];
    let bestIndex = 0;
    let bestSpacing = -1;
    patchCandidates.forEach((cell, index) => {
      const spacing = Math.min(...anchors.map((anchor) => manhattan(cell, anchor)));
      if (spacing > bestSpacing) {
        bestSpacing = spacing;
        bestIndex = index;
      }
    });
    patches.push(patchCandidates.splice(bestIndex, 1)[0]);
  }

  return { open, playerStart, core, virusStarts, patches, portals };
}

function listOpen(open: boolean[][]): Cell[] {
  const cells: Cell[] = [];
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      if (open[row][col]) cells.push({ row, col });
    }
  }
  return cells;
}

function nearestOpen(open: boolean[][], target: Cell): Cell {
  let best = { row: 1, col: 1 };
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const cell of listOpen(open)) {
    const distance = manhattan(cell, target);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = cell;
    }
  }
  return best;
}

function manhattan(a: Cell, b: Cell): number {
  return Math.abs(a.row - b.row) + Math.abs(a.col - b.col);
}

function createsOpenBlock(open: boolean[][], changed: Cell): boolean {
  for (let top = changed.row - 1; top <= changed.row; top += 1) {
    for (let left = changed.col - 1; left <= changed.col; left += 1) {
      if (
        top >= 0 &&
        left >= 0 &&
        top + 1 < ROWS &&
        left + 1 < COLS &&
        open[top][left] &&
        open[top][left + 1] &&
        open[top + 1][left] &&
        open[top + 1][left + 1]
      ) {
        return true;
      }
    }
  }
  return false;
}

function pixel(cell: Cell): { x: number; y: number } {
  return {
    x: cell.col * TILE + TILE / 2,
    y: cell.row * TILE + TILE / 2
  };
}

class BitsAndBytesScene extends Phaser.Scene {
  private maze!: Maze;
  private player!: Mover;
  private viruses: Virus[] = [];
  private wallGraphics!: Phaser.GameObjects.Graphics;
  private bitGroup!: Phaser.GameObjects.Group;
  private patchGroup!: Phaser.GameObjects.Group;
  private portalGroup!: Phaser.GameObjects.Group;
  private bitObjects = new Map<string, Phaser.GameObjects.Text>();
  private patchObjects = new Map<string, Phaser.GameObjects.Container>();
  private desiredDirection: Direction = "right";
  private score = 0;
  private highScore = readHighScore();
  private lives = 3;
  private sector = 1;
  private started = false;
  private roundActive = false;
  private paused = false;
  private transitionToken = 0;
  private patchUntil = 0;
  private quarantineChain = 0;
  private mode: "roam" | "hunt" = "roam";
  private modeIndex = 0;
  private modeElapsed = 0;
  private roundElapsed = 0;
  private readonly modeSchedule = [
    { mode: "roam" as const, duration: 7000 },
    { mode: "hunt" as const, duration: 19000 },
    { mode: "roam" as const, duration: 6000 },
    { mode: "hunt" as const, duration: 24000 },
    { mode: "roam" as const, duration: 4500 },
    { mode: "hunt" as const, duration: Number.POSITIVE_INFINITY }
  ];
  private audioContext: AudioContext | null = null;
  private swipeStart: Phaser.Math.Vector2 | null = null;
  private playerTween: Phaser.Tweens.Tween | null = null;

  constructor() {
    super("bits-and-bytes");
  }

  create(): void {
    this.makeTextures();
    this.wallGraphics = this.add.graphics();
    this.bitGroup = this.add.group();
    this.patchGroup = this.add.group();
    this.portalGroup = this.add.group();
    this.maze = buildMaze(this.sector);
    this.drawMaze();

    const playerPosition = pixel(this.maze.playerStart);
    this.player = {
      ...this.maze.playerStart,
      sprite: this.add.sprite(playerPosition.x, playerPosition.y, "byte").setDepth(5),
      direction: "right",
      moving: false
    };

    const corners: Cell[] = [
      { row: 1, col: COLS - 2 },
      { row: ROWS - 2, col: COLS - 2 },
      { row: ROWS - 2, col: 1 },
      { row: 1, col: 1 }
    ];

    this.viruses = VIRUS_NAMES.map((name, index) => {
      const start = this.maze.virusStarts[index];
      const position = pixel(start);
      return {
        ...start,
        sprite: this.add.sprite(position.x, position.y, `virus-${name}`).setDepth(5),
        direction: index % 2 === 0 ? "left" : "right",
        moving: false,
        name,
        corner: corners[index],
        state: "waiting",
        safeUntil: 0,
        releaseDelay: 900 + index * 3500
      };
    });

    this.resetPositions();
    this.bindKeyboard();
    this.bindSwipe();
    this.updateHud();
    statusElement.textContent = "Ready";
  }

  update(_time: number, delta: number): void {
    if (!this.started || !this.roundActive || this.paused) return;

    if (this.patchUntil > 0 && this.time.now >= this.patchUntil) {
      this.patchUntil = 0;
      this.quarantineChain = 0;
      this.refreshVirusTextures();
      this.setStatus(this.mode === "hunt" ? "Viruses hunting" : "Viruses roaming");
    }

    this.roundElapsed += delta;
    this.viruses.forEach((virus) => {
      if (virus.state === "waiting" && this.roundElapsed >= virus.releaseDelay) {
        this.releaseVirus(virus);
      }
    });

    this.modeElapsed += delta;
    const phase = this.modeSchedule[this.modeIndex];
    if (this.modeElapsed >= phase.duration && this.modeIndex < this.modeSchedule.length - 1) {
      this.modeElapsed = 0;
      this.modeIndex += 1;
      this.mode = this.modeSchedule[this.modeIndex].mode;
      for (const virus of this.viruses) {
        if (virus.state === "active") virus.direction = OPPOSITE[virus.direction];
      }
      this.setStatus(this.mode === "hunt" ? "Viruses hunting" : "Viruses roaming");
    }

    for (const virus of this.viruses) {
      const distance = Phaser.Math.Distance.Between(
        this.player.sprite.x,
        this.player.sprite.y,
        virus.sprite.x,
        virus.sprite.y
      );
      if (distance < TILE * 0.58) this.handleVirusContact(virus);
    }
  }

  public startGame(): void {
    this.ensureAudio();
    this.started = true;
    this.roundActive = true;
    this.paused = false;
    pauseButton.textContent = "Pause";
    this.setStatus("Collect the bits");
    this.movePlayer();
  }

  public setDesiredDirection(direction: Direction): void {
    this.desiredDirection = direction;
    if (this.tryForgivingTurn(direction)) return;
    if (this.started && this.roundActive && !this.paused && !this.player.moving) {
      this.movePlayer();
    }
  }

  public togglePause(): void {
    if (!this.started || !this.roundActive) return;
    this.paused = !this.paused;
    if (this.paused) {
      this.tweens.pauseAll();
      pauseButton.textContent = "Resume";
      this.setStatus("Paused");
    } else {
      this.tweens.resumeAll();
      pauseButton.textContent = "Pause";
      this.setStatus(this.patchUntil > this.time.now ? "Patch active" : "Running");
      if (!this.player.moving) this.movePlayer();
      this.viruses.forEach((virus) => {
        if (!virus.moving) this.moveVirus(virus);
      });
    }
  }

  public restartGame(): void {
    this.transitionToken += 1;
    this.tweens.killAll();
    this.playerTween = null;
    this.score = 0;
    this.lives = 3;
    this.sector = 1;
    this.started = true;
    this.paused = false;
    this.patchUntil = 0;
    this.quarantineChain = 0;
    pauseButton.textContent = "Pause";
    this.loadSector();
    this.roundActive = true;
    this.setStatus("Collect the bits");
    this.movePlayer();
  }

  private bindKeyboard(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) return;

    const mapping: Record<string, Direction> = {
      UP: "up",
      W: "up",
      DOWN: "down",
      S: "down",
      LEFT: "left",
      A: "left",
      RIGHT: "right",
      D: "right"
    };

    Object.entries(mapping).forEach(([key, direction]) => {
      keyboard.on(`keydown-${key}`, (event: KeyboardEvent) => {
        event.preventDefault();
        this.setDesiredDirection(direction);
      });
    });
    keyboard.on("keydown-P", () => this.togglePause());
  }

  private bindSwipe(): void {
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.swipeStart = new Phaser.Math.Vector2(pointer.x, pointer.y);
    });
    this.input.on("pointerup", (pointer: Phaser.Input.Pointer) => {
      if (!this.swipeStart) return;
      const deltaX = pointer.x - this.swipeStart.x;
      const deltaY = pointer.y - this.swipeStart.y;
      this.swipeStart = null;
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 20) return;
      this.setDesiredDirection(
        Math.abs(deltaX) > Math.abs(deltaY)
          ? deltaX > 0
            ? "right"
            : "left"
          : deltaY > 0
            ? "down"
            : "up"
      );
    });
  }

  private makeTextures(): void {
    const graphics = this.make.graphics({ x: 0, y: 0 }, false);

    graphics.fillStyle(0x66f2ce);
    graphics.fillRoundedRect(2, 2, 24, 24, 7);
    graphics.lineStyle(2, 0x071116, 0.35);
    graphics.strokeRoundedRect(4, 4, 20, 20, 5);
    graphics.fillStyle(0x071116);
    graphics.fillCircle(9, 11, 2);
    graphics.fillCircle(19, 11, 2);
    graphics.lineStyle(2, 0x071116);
    graphics.lineBetween(10, 19, 18, 19);
    graphics.generateTexture("byte", 28, 28);
    graphics.clear();

    VIRUS_NAMES.forEach((name) => {
      this.drawVirusTexture(graphics, VIRUS_COLORS[name]);
      graphics.generateTexture(`virus-${name}`, 28, 28);
      graphics.clear();
    });

    this.drawVirusTexture(graphics, 0x46657e);
    graphics.lineStyle(2, 0xc4f8ff);
    graphics.lineBetween(7, 8, 11, 12);
    graphics.lineBetween(11, 8, 7, 12);
    graphics.lineBetween(17, 8, 21, 12);
    graphics.lineBetween(21, 8, 17, 12);
    graphics.generateTexture("virus-vulnerable", 28, 28);
    graphics.clear();

    graphics.lineStyle(2, 0x66f2ce);
    graphics.strokeCircle(14, 14, 9);
    graphics.lineBetween(7, 21, 21, 7);
    graphics.fillStyle(0x66f2ce);
    graphics.fillCircle(14, 14, 3);
    graphics.generateTexture("virus-returning", 28, 28);
    graphics.destroy();
  }

  private drawVirusTexture(graphics: Phaser.GameObjects.Graphics, color: number): void {
    graphics.fillStyle(color);
    const spikes = [
      [14, 1, 11, 6, 17, 6],
      [27, 14, 22, 11, 22, 17],
      [14, 27, 11, 22, 17, 22],
      [1, 14, 6, 11, 6, 17]
    ];
    spikes.forEach(([x1, y1, x2, y2, x3, y3]) => {
      graphics.fillTriangle(x1, y1, x2, y2, x3, y3);
    });
    graphics.fillCircle(14, 14, 10);
    graphics.fillStyle(0xf4ffff);
    graphics.fillCircle(10, 11, 3);
    graphics.fillCircle(18, 11, 3);
    graphics.fillStyle(0x071116);
    graphics.fillCircle(10, 12, 1.4);
    graphics.fillCircle(18, 12, 1.4);
    graphics.lineStyle(1.6, 0x071116);
    graphics.lineBetween(9, 19, 12, 17);
    graphics.lineBetween(12, 17, 15, 19);
    graphics.lineBetween(15, 19, 19, 17);
  }

  private drawMaze(): void {
    this.wallGraphics.clear();
    this.wallGraphics.setDepth(0);
    this.bitGroup.clear(true, true);
    this.patchGroup.clear(true, true);
    this.portalGroup.clear(true, true);
    this.bitObjects.clear();
    this.patchObjects.clear();

    this.wallGraphics.fillStyle(0x0d2027, 1);
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        if (this.maze.open[row][col]) continue;
        this.wallGraphics.fillRoundedRect(col * TILE + 2, row * TILE + 2, TILE - 4, TILE - 4, 5);
        if ((row * 11 + col * 7 + this.sector) % 13 === 0) {
          this.wallGraphics.fillStyle(0x1e4650, 1);
          this.wallGraphics.fillCircle(col * TILE + TILE / 2, row * TILE + TILE / 2, 2);
          this.wallGraphics.fillStyle(0x0d2027, 1);
        }
      }
    }

    const excluded = new Set([
      keyOf(this.maze.playerStart),
      keyOf(this.maze.core),
      ...this.maze.virusStarts.map(keyOf),
      ...this.maze.patches.map(keyOf),
      ...this.maze.portals.flatMap((portal) => [keyOf(portal.a), keyOf(portal.b)])
    ]);

    for (const cell of listOpen(this.maze.open)) {
      if (excluded.has(keyOf(cell))) continue;
      const position = pixel(cell);
      const bit = this.add
        .text(position.x, position.y, (cell.row + cell.col) % 2 === 0 ? "1" : "0", {
          fontFamily: '"DM Mono", monospace',
          fontSize: "9px",
          color: "#5f8f91"
        })
        .setOrigin(0.5)
        .setDepth(1);
      this.bitGroup.add(bit);
      this.bitObjects.set(keyOf(cell), bit);
    }

    this.maze.patches.forEach((cell, index) => {
      const position = pixel(cell);
      const glow = this.add.circle(0, 0, 9, 0x66f2ce, 0.16);
      const ring = this.add.circle(0, 0, 6, 0x66f2ce, 1).setStrokeStyle(1, 0xcafff0);
      const plus = this.add
        .text(0, -0.5, "+", {
          fontFamily: '"DM Mono", monospace',
          fontSize: "11px",
          color: "#071116",
          fontStyle: "bold"
        })
        .setOrigin(0.5);
      const container = this.add.container(position.x, position.y, [glow, ring, plus]).setDepth(2);
      this.tweens.add({
        targets: glow,
        scale: 1.65,
        alpha: 0,
        duration: 850 + index * 80,
        yoyo: true,
        repeat: -1
      });
      this.patchGroup.add(container);
      this.patchObjects.set(keyOf(cell), container);
    });

    this.maze.portals.forEach((portal, pairIndex) => {
      [portal.a, portal.b].forEach((cell, endIndex) => {
        const position = pixel(cell);
        const halo = this.add.circle(0, 0, 10, portal.color, 0.13);
        const outer = this.add.circle(0, 0, 8, 0x071216, 1).setStrokeStyle(2, portal.color, 0.95);
        const inner = this.add.circle(0, 0, 5, portal.color, 0.18).setStrokeStyle(1, portal.color, 0.65);
        const label = this.add
          .text(0, -0.5, portal.label, {
            fontFamily: '"DM Mono", monospace',
            fontSize: "8px",
            color: pairIndex === 0 ? "#bdf8ff" : "#e6c8ff",
            fontStyle: "bold"
          })
          .setOrigin(0.5);
        const container = this.add
          .container(position.x, position.y, [halo, outer, inner, label])
          .setDepth(2);
        this.tweens.add({
          targets: halo,
          scale: 1.45,
          alpha: 0.03,
          duration: 900 + endIndex * 120,
          yoyo: true,
          repeat: -1
        });
        this.portalGroup.add(container);
      });
    });

    const corePosition = pixel(this.maze.core);
    this.wallGraphics.lineStyle(1, 0x66f2ce, 0.28);
    this.wallGraphics.strokeCircle(corePosition.x, corePosition.y, 9);
    this.wallGraphics.lineBetween(corePosition.x - 6, corePosition.y, corePosition.x + 6, corePosition.y);
    this.wallGraphics.lineBetween(corePosition.x, corePosition.y - 6, corePosition.x, corePosition.y + 6);
  }

  private movePlayer(): void {
    if (!this.started || !this.roundActive || this.paused || this.player.moving) return;

    if (this.canMove(this.player, this.desiredDirection)) {
      this.player.direction = this.desiredDirection;
    }
    if (!this.canMove(this.player, this.player.direction)) return;

    const vector = VECTORS[this.player.direction];
    const destination = { row: this.player.row + vector.row, col: this.player.col + vector.col };
    this.tweenPlayerTo(destination, this.playerStepDuration());
  }

  private tweenPlayerTo(destination: Cell, duration: number): void {
    const position = pixel(destination);
    const token = this.transitionToken;
    this.player.moving = true;

    this.playerTween = this.tweens.add({
      targets: this.player.sprite,
      x: position.x,
      y: position.y,
      duration,
      ease: "Linear",
      onComplete: () => {
        if (token !== this.transitionToken) return;
        this.playerTween = null;
        const arrival = this.teleportDestination(destination);
        this.player.row = arrival.row;
        this.player.col = arrival.col;
        if (keyOf(arrival) !== keyOf(destination)) {
          this.player.sprite.setPosition(pixel(arrival).x, pixel(arrival).y);
          const exitDirection = this.deadEndExit(arrival);
          if (exitDirection) {
            this.player.direction = exitDirection;
            this.desiredDirection = exitDirection;
          }
          this.playPortalEffect(destination, arrival, true);
        }
        this.player.moving = false;
        this.collectAt(arrival);
        this.movePlayer();
      }
    });
  }

  private tryForgivingTurn(direction: Direction): boolean {
    if (
      !this.started ||
      !this.roundActive ||
      this.paused ||
      !this.player.moving ||
      !this.playerTween
    ) {
      return false;
    }

    if (direction === this.player.direction) return false;

    if (direction === OPPOSITE[this.player.direction]) {
      this.playerTween.stop();
      this.playerTween = null;
      this.player.direction = direction;
      const center = pixel(this.player);
      const distance = Phaser.Math.Distance.Between(
        this.player.sprite.x,
        this.player.sprite.y,
        center.x,
        center.y
      );
      if (distance < 1) {
        this.player.moving = false;
        this.movePlayer();
      } else {
        this.tweenPlayerTo(
          { row: this.player.row, col: this.player.col },
          Math.max(24, this.playerStepDuration() * (distance / TILE))
        );
      }
      return true;
    }

    const cornerWindow = this.playerTween.progress <= 0.48;
    if (!cornerWindow || !this.canMove(this.player, direction)) return false;

    this.playerTween.stop();
    this.playerTween = null;
    this.player.direction = direction;
    const vector = VECTORS[direction];
    const destination = {
      row: this.player.row + vector.row,
      col: this.player.col + vector.col
    };
    const target = pixel(destination);
    const distance = Phaser.Math.Distance.Between(
      this.player.sprite.x,
      this.player.sprite.y,
      target.x,
      target.y
    );
    this.tweenPlayerTo(
      destination,
      Math.max(45, this.playerStepDuration() * (distance / TILE))
    );
    return true;
  }

  private playerStepDuration(): number {
    const profile = this.speedProfile();
    const speed = this.patchUntil > this.time.now ? profile.patchPlayer : profile.player;
    return FULL_SPEED_STEP_MS / speed;
  }

  private moveVirus(virus: Virus): void {
    if (
      !this.started ||
      !this.roundActive ||
      this.paused ||
      virus.moving ||
      virus.state === "quarantined" ||
      virus.state === "waiting"
    ) {
      return;
    }

    const direction = this.chooseVirusDirection(virus);
    if (!direction) {
      this.time.delayedCall(40, () => this.moveVirus(virus));
      return;
    }

    virus.direction = direction;
    const vector = VECTORS[direction];
    const destination = { row: virus.row + vector.row, col: virus.col + vector.col };
    const position = pixel(destination);
    const token = this.transitionToken;
    virus.moving = true;

    const profile = this.speedProfile();
    let duration = FULL_SPEED_STEP_MS / profile.virus;
    if (virus.state === "returning") duration = 76;
    else if (this.patchUntil > this.time.now) {
      duration = FULL_SPEED_STEP_MS / profile.vulnerableVirus;
    }

    this.tweens.add({
      targets: virus.sprite,
      x: position.x,
      y: position.y,
      duration,
      ease: "Linear",
      onComplete: () => {
        if (token !== this.transitionToken) return;
        const arrival = this.teleportDestination(destination);
        virus.row = arrival.row;
        virus.col = arrival.col;
        if (keyOf(arrival) !== keyOf(destination)) {
          virus.sprite.setPosition(pixel(arrival).x, pixel(arrival).y);
          const exitDirection = this.deadEndExit(arrival);
          if (exitDirection) virus.direction = exitDirection;
          this.playPortalEffect(destination, arrival, false);
        }
        virus.moving = false;

        if (virus.state === "returning" && keyOf(virus) === keyOf(this.maze.core)) {
          this.containVirus(virus, token);
          return;
        }
        this.moveVirus(virus);
      }
    });
  }

  private chooseVirusDirection(virus: Virus): Direction | null {
    let options = DIRECTIONS.filter((direction) => this.canMove(virus, direction));
    if (options.length === 0) return null;

    const withoutReverse = options.filter((direction) => direction !== OPPOSITE[virus.direction]);
    if (withoutReverse.length > 0) options = withoutReverse;

    if (virus.state === "returning") {
      return this.bestDirection(options, virus, this.maze.core, false);
    }

    if (this.patchUntil > this.time.now) {
      return this.bestDirection(options, virus, { row: this.player.row, col: this.player.col }, true);
    }

    const target = this.mode === "roam" ? virus.corner : this.huntTarget(virus);
    return this.bestDirection(options, virus, target, false);
  }

  private huntTarget(virus: Virus): Cell {
    const player = { row: this.player.row, col: this.player.col };
    if (virus.name === "worm") return player;

    if (virus.name === "trojan") {
      return this.projectAhead(player, this.player.direction, 4);
    }

    if (virus.name === "spyware") {
      const ahead = this.projectAhead(player, this.player.direction, 2);
      const worm = this.viruses[0];
      return {
        row: Phaser.Math.Clamp(ahead.row * 2 - worm.row, 1, ROWS - 2),
        col: Phaser.Math.Clamp(ahead.col * 2 - worm.col, 1, COLS - 2)
      };
    }

    if (manhattan(virus, player) > 8) return player;
    const ahead = this.projectAhead(player, this.player.direction, 3);
    return {
      row: Phaser.Math.Clamp(ahead.row + VECTORS[this.player.direction].col * 4, 1, ROWS - 2),
      col: Phaser.Math.Clamp(ahead.col - VECTORS[this.player.direction].row * 4, 1, COLS - 2)
    };
  }

  private projectAhead(start: Cell, direction: Direction, amount: number): Cell {
    const vector = VECTORS[direction];
    return {
      row: Phaser.Math.Clamp(start.row + vector.row * amount, 1, ROWS - 2),
      col: Phaser.Math.Clamp(start.col + vector.col * amount, 1, COLS - 2)
    };
  }

  private bestDirection(
    options: Direction[],
    mover: Cell,
    rawTarget: Cell,
    maximize: boolean
  ): Direction {
    const target = nearestOpen(this.maze.open, rawTarget);
    const distances = this.distanceMap(target);
    const scored = options.map((direction) => {
      const vector = VECTORS[direction];
      const row = mover.row + vector.row;
      const col = mover.col + vector.col;
      return { direction, distance: distances[row][col] };
    });
    scored.sort((a, b) => (maximize ? b.distance - a.distance : a.distance - b.distance));

    const bestDistance = scored[0].distance;
    const tied = scored.filter((item) => item.distance === bestDistance);
    return tied[Math.floor(Math.random() * tied.length)].direction;
  }

  private distanceMap(target: Cell): number[][] {
    const distances = Array.from({ length: ROWS }, () =>
      Array(COLS).fill(Number.POSITIVE_INFINITY) as number[]
    );
    const queue: Cell[] = [target];
    distances[target.row][target.col] = 0;

    for (let index = 0; index < queue.length; index += 1) {
      const current = queue[index];
      for (const direction of DIRECTIONS) {
        const vector = VECTORS[direction];
        const row = current.row + vector.row;
        const col = current.col + vector.col;
        if (
          row < 0 ||
          row >= ROWS ||
          col < 0 ||
          col >= COLS ||
          !this.maze.open[row][col] ||
          Number.isFinite(distances[row][col])
        ) {
          continue;
        }
        distances[row][col] = distances[current.row][current.col] + 1;
        queue.push({ row, col });
      }

      const portalExit = this.teleportDestination(current);
      if (
        keyOf(portalExit) !== keyOf(current) &&
        !Number.isFinite(distances[portalExit.row][portalExit.col])
      ) {
        distances[portalExit.row][portalExit.col] = distances[current.row][current.col] + 1;
        queue.push(portalExit);
      }
    }
    return distances;
  }

  private teleportDestination(cell: Cell): Cell {
    for (const portal of this.maze.portals) {
      if (keyOf(cell) === keyOf(portal.a)) return portal.b;
      if (keyOf(cell) === keyOf(portal.b)) return portal.a;
    }
    return cell;
  }

  private playPortalEffect(origin: Cell, destination: Cell, playerUsedPortal: boolean): void {
    const portal = this.maze.portals.find(
      (pair) =>
        keyOf(pair.a) === keyOf(origin) ||
        keyOf(pair.b) === keyOf(origin)
    );
    if (!portal) return;

    [origin, destination].forEach((cell) => {
      const position = pixel(cell);
      const ring = this.add
        .circle(position.x, position.y, 5, portal.color, 0.12)
        .setStrokeStyle(2, portal.color, 1)
        .setDepth(7);
      this.tweens.add({
        targets: ring,
        radius: 17,
        alpha: 0,
        duration: 320,
        onComplete: () => ring.destroy()
      });
    });

    if (playerUsedPortal) {
      this.showNotice(`Portal ${portal.label}`, 420);
      this.soundEffect(portal.label === "A" ? 760 : 920, 0.08, 0.045);
    }
  }

  private canMove(mover: Cell, direction: Direction): boolean {
    const vector = VECTORS[direction];
    const row = mover.row + vector.row;
    const col = mover.col + vector.col;
    return row >= 0 && row < ROWS && col >= 0 && col < COLS && this.maze.open[row][col];
  }

  private speedProfile(): {
    player: number;
    virus: number;
    patchPlayer: number;
    vulnerableVirus: number;
  } {
    if (this.sector === 1) {
      return { player: 0.8, virus: 0.75, patchPlayer: 0.9, vulnerableVirus: 0.5 };
    }
    if (this.sector <= 4) {
      return { player: 0.9, virus: 0.85, patchPlayer: 0.95, vulnerableVirus: 0.55 };
    }
    return { player: 1, virus: 0.95, patchPlayer: 1, vulnerableVirus: 0.6 };
  }

  private deadEndExit(cell: Cell): Direction | null {
    return DIRECTIONS.find((direction) => this.canMove(cell, direction)) ?? null;
  }

  private collectAt(cell: Cell): void {
    const key = keyOf(cell);
    const bit = this.bitObjects.get(key);
    if (bit) {
      bit.destroy();
      this.bitObjects.delete(key);
      this.addScore(10);
      this.soundEffect(560 + ((cell.row + cell.col) % 2) * 110, 0.025, 0.025);
      if (this.bitObjects.size === 0) {
        this.completeSector();
        return;
      }
    }

    const patch = this.patchObjects.get(key);
    if (patch) {
      patch.destroy();
      this.patchObjects.delete(key);
      this.addScore(50);
      this.patchUntil = this.time.now + 7200;
      this.quarantineChain = 0;
      this.refreshVirusTextures();
      this.setStatus("Patch active");
      this.soundEffect(880, 0.12, 0.06);
    }
  }

  private handleVirusContact(virus: Virus): void {
    if (!this.roundActive || virus.state !== "active" || virus.safeUntil > this.time.now) return;

    if (this.patchUntil > this.time.now) {
      virus.state = "returning";
      virus.sprite.setTexture("virus-returning").setAlpha(1);
      this.quarantineChain += 1;
      this.addScore(200 * 2 ** (this.quarantineChain - 1));
      this.showNotice(`${virus.name} sent to quarantine`);
      this.soundEffect(250 + this.quarantineChain * 80, 0.14, 0.08);
      return;
    }

    this.loseLife();
  }

  private loseLife(): void {
    if (!this.roundActive) return;
    this.roundActive = false;
    this.transitionToken += 1;
    this.tweens.killAll();
    this.playerTween = null;
    this.player.moving = false;
    this.viruses.forEach((virus) => {
      virus.moving = false;
    });
    this.lives -= 1;
    this.updateHud();
    this.soundEffect(115, 0.32, 0.12);

    if (this.lives <= 0) {
      this.setStatus("Program stopped");
      this.showNotice("Out of bytes", 800);
      this.time.delayedCall(850, () => {
        finalScoreElement.textContent = `Score: ${this.score.toLocaleString()}`;
        if (!gameOverDialog.open) gameOverDialog.showModal();
      });
      return;
    }

    this.showNotice("Byte lost — rebooting", 850);
    this.time.delayedCall(900, () => {
      this.resetPositions();
      this.roundActive = true;
      this.setStatus("Reconnected");
      this.movePlayer();
    });
  }

  private completeSector(): void {
    if (!this.roundActive) return;
    this.roundActive = false;
    this.transitionToken += 1;
    this.tweens.killAll();
    this.playerTween = null;
    this.addScore(1000 + this.sector * 250);
    this.showNotice(`Sector ${this.sector} cleared`, 1200);
    this.soundEffect(720, 0.1, 0.07);
    this.time.delayedCall(1250, () => {
      this.sector += 1;
      this.loadSector();
      this.roundActive = true;
      this.setStatus("New circuit loaded");
      this.movePlayer();
    });
  }

  private loadSector(): void {
    this.transitionToken += 1;
    this.maze = buildMaze(this.sector);
    this.patchUntil = 0;
    this.quarantineChain = 0;
    this.modeIndex = 0;
    this.mode = "roam";
    this.modeElapsed = 0;
    this.roundElapsed = 0;
    this.drawMaze();
    this.resetPositions();
    this.updateHud();
  }

  private resetPositions(): void {
    this.patchUntil = 0;
    this.quarantineChain = 0;
    this.player.row = this.maze.playerStart.row;
    this.player.col = this.maze.playerStart.col;
    this.player.direction = "right";
    this.desiredDirection = "right";
    this.player.moving = false;
    this.playerTween = null;
    this.player.sprite.setPosition(pixel(this.player).x, pixel(this.player).y).setAlpha(1);

    this.roundElapsed = 0;
    const corePosition = pixel(this.maze.core);
    const offsets = [
      { x: -5, y: -5 },
      { x: 5, y: -5 },
      { x: -5, y: 5 },
      { x: 5, y: 5 }
    ];
    this.viruses.forEach((virus, index) => {
      virus.row = this.maze.core.row;
      virus.col = this.maze.core.col;
      virus.direction = index % 2 === 0 ? "left" : "right";
      virus.moving = false;
      virus.state = "waiting";
      virus.releaseDelay = 900 + index * 3500;
      virus.safeUntil = Number.POSITIVE_INFINITY;
      virus.sprite
        .setPosition(corePosition.x + offsets[index].x, corePosition.y + offsets[index].y)
        .setTexture("virus-returning")
        .setScale(0.58)
        .setAlpha(0.58);
    });
  }

  private refreshVirusTextures(): void {
    this.viruses.forEach((virus) => {
      if (
        virus.state === "returning" ||
        virus.state === "quarantined" ||
        virus.state === "waiting"
      ) {
        virus.sprite.setTexture("virus-returning");
      } else if (this.patchUntil > this.time.now) {
        virus.sprite.setTexture("virus-vulnerable");
      } else {
        virus.sprite.setTexture(`virus-${virus.name}`);
      }
    });
  }

  private containVirus(virus: Virus, token: number): void {
    virus.state = "quarantined";
    virus.moving = false;
    const index = this.viruses.indexOf(virus);
    const offsets = [
      { x: -5, y: -5 },
      { x: 5, y: -5 },
      { x: -5, y: 5 },
      { x: 5, y: 5 }
    ];
    const corePosition = pixel(this.maze.core);
    virus.sprite
      .setPosition(corePosition.x + offsets[index].x, corePosition.y + offsets[index].y)
      .setTexture("virus-returning")
      .setScale(0.58)
      .setAlpha(0.58);

    this.time.delayedCall(4000, () => {
      if (
        token !== this.transitionToken ||
        !this.roundActive ||
        virus.state !== "quarantined"
      ) {
        return;
      }
      virus.state = "active";
      virus.safeUntil = this.time.now + 1100;
      virus.sprite
        .setPosition(corePosition.x, corePosition.y)
        .setTexture(this.patchUntil > this.time.now ? "virus-vulnerable" : `virus-${virus.name}`)
        .setScale(0.35)
        .setAlpha(0.25);
      this.tweens.add({
        targets: virus.sprite,
        scale: 1,
        alpha: 1,
        duration: 500,
        ease: "Back.Out",
        onComplete: () => this.moveVirus(virus)
      });
    });
  }

  private releaseVirus(virus: Virus): void {
    if (virus.state !== "waiting" || !this.roundActive || this.paused) return;
    const corePosition = pixel(this.maze.core);
    virus.state = "active";
    virus.safeUntil = this.time.now + 1000;
    virus.row = this.maze.core.row;
    virus.col = this.maze.core.col;
    virus.sprite
      .setPosition(corePosition.x, corePosition.y)
      .setTexture(this.patchUntil > this.time.now ? "virus-vulnerable" : `virus-${virus.name}`)
      .setScale(0.35)
      .setAlpha(0.25);
    this.tweens.add({
      targets: virus.sprite,
      scale: 1,
      alpha: 1,
      duration: 480,
      ease: "Back.Out",
      onComplete: () => this.moveVirus(virus)
    });
  }

  private addScore(points: number): void {
    this.score += points;
    if (this.score > this.highScore) {
      this.highScore = this.score;
      try {
        localStorage.setItem("bits-and-bytes-high-score", String(this.highScore));
      } catch {
        // High scores still work for this session when storage is unavailable.
      }
    }
    this.updateHud();
  }

  private updateHud(): void {
    scoreElement.textContent = this.score.toLocaleString();
    highScoreElement.textContent = this.highScore.toLocaleString();
    sectorElement.textContent = String(this.sector);
    livesElement.textContent = Array.from({ length: Math.max(this.lives, 0) }, () => "■").join(" ");
    livesElement.setAttribute("aria-label", `${this.lives} ${this.lives === 1 ? "life" : "lives"}`);
  }

  private setStatus(message: string): void {
    statusElement.textContent = message;
  }

  private showNotice(message: string, duration = 650): void {
    noticeElement.textContent = message;
    noticeElement.hidden = false;
    window.setTimeout(() => {
      if (noticeElement.textContent === message) noticeElement.hidden = true;
    }, duration);
  }

  private ensureAudio(): void {
    if (!this.audioContext) {
      try {
        this.audioContext = new AudioContext();
      } catch {
        this.audioContext = null;
      }
    }
    if (this.audioContext?.state === "suspended") void this.audioContext.resume();
  }

  private soundEffect(frequency: number, duration: number, volume: number): void {
    if (!this.audioContext) return;
    const oscillator = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    oscillator.type = "square";
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.audioContext.currentTime + duration);
    oscillator.connect(gain);
    gain.connect(this.audioContext.destination);
    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + duration);
  }
}

function readHighScore(): number {
  try {
    const value = Number(localStorage.getItem("bits-and-bytes-high-score"));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

const game = new Phaser.Game({
  type: Phaser.AUTO,
  width: WIDTH,
  height: HEIGHT,
  parent: "game",
  backgroundColor: "#071216",
  render: {
    antialias: true,
    pixelArt: false
  },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH
  },
  scene: BitsAndBytesScene
});

function scene(): BitsAndBytesScene | null {
  return game.scene.getScene("bits-and-bytes") as BitsAndBytesScene | null;
}

requiredButton("start-button").addEventListener("click", () => {
  startDialog.close();
  scene()?.startGame();
});

requiredButton("retry-button").addEventListener("click", () => {
  gameOverDialog.close();
  scene()?.restartGame();
});

pauseButton.addEventListener("click", () => scene()?.togglePause());

document.querySelectorAll<HTMLButtonElement>("[data-direction]").forEach((button) => {
  const direction = button.dataset.direction as Direction;
  const move = (event: Event) => {
    event.preventDefault();
    scene()?.setDesiredDirection(direction);
  };
  button.addEventListener("pointerdown", move);
});

window.addEventListener("blur", () => {
  const activeScene = scene();
  if (activeScene && !document.querySelector("dialog[open]")) activeScene.togglePause();
});

window.addEventListener("load", () => {
  if (!startDialog.open) startDialog.showModal();
});
